/**
 * useOneShotHighlight: the "you landed here" cue for a row the app just
 * jumped to (a deep link, "I'll fix it", a search hit). DESIGN.md section 4.2's
 * one-shot timing, applied to a background tint instead of the unlock glow:
 *
 *   - `highlight(id)` tints that row for `ONE_SHOT_HIGHLIGHT_MS` (1200 ms, the
 *     section 4.2 clear), then it clears itself. Never ambient: a second call
 *     restarts the clock for the new row.
 *   - The tint is a background colour only (`ONE_SHOT_HIGHLIGHT_CLASS`), with
 *     `ONE_SHOT_HIGHLIGHT_TRANSITION` (200 ms ease-out, section 5) so it fades
 *     rather than pops. It never touches `box-shadow`, so it cannot fight a
 *     focus ring, and it is not clipped by an `overflow-hidden` list.
 *   - Reduced motion: the transition class carries `motion-reduce:transition-none`,
 *     so the tint appears and clears without a fade. The tint itself is kept:
 *     it is information (which row), not decoration.
 *
 * The caller moves focus to the row's primary control itself; colour is never
 * the only signal (section 16).
 */
import { useCallback, useEffect, useState } from 'react';

export const ONE_SHOT_HIGHLIGHT_MS = 1200;

/** The tint a highlighted row carries (brand purple at a light tint, section 1). */
export const ONE_SHOT_HIGHLIGHT_CLASS = 'bg-threadheads-purple/[0.08]';

/** Put this on the row at all times, so the tint fades in and out. */
export const ONE_SHOT_HIGHLIGHT_TRANSITION =
  'transition-[background-color] duration-200 ease-out motion-reduce:transition-none';

export interface OneShotHighlight<T> {
  /** The row currently highlighted, or null. */
  highlighted: T | null;
  /** Highlight `id` now; it clears itself after `ONE_SHOT_HIGHLIGHT_MS`. */
  highlight: (id: T) => void;
  /** Clear at once (for example when the row unmounts). */
  clear: () => void;
}

export function useOneShotHighlight<T = string>(durationMs: number = ONE_SHOT_HIGHLIGHT_MS): OneShotHighlight<T> {
  const [highlighted, setHighlighted] = useState<T | null>(null);
  // A counter, so highlighting the SAME row twice still restarts the clock.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (highlighted === null) return undefined;
    const timer = window.setTimeout(() => setHighlighted(null), durationMs);
    return () => window.clearTimeout(timer);
  }, [highlighted, tick, durationMs]);

  const highlight = useCallback((id: T) => {
    setHighlighted(id);
    setTick((n) => n + 1);
  }, []);
  const clear = useCallback(() => setHighlighted(null), []);

  return { highlighted, highlight, clear };
}
